import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentCompanyId } from './useCurrentCompanyId';
import { usePermissions } from './usePermissions';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/utils/logger';
import { useToast } from '@/hooks/use-toast';
import {
  FEATURE_LABELS,
  ROLE_LABELS,
  ASSIGNABLE_ROLES,
  CONFIGURABLE_ROLES,
  FEATURE_KEYS,
  FEATURE_MODULES,
} from '@/constants/permissions';

// Re-export constants for backward compatibility
export { ROLE_LABELS, ASSIGNABLE_ROLES };

/**
 * Hook para verificar se o usuário atual tem permissão para uma feature específica.
 * Resolve inteiramente em memória via rolePermissionsMap do AuthContext (zero roundtrips).
 * Master tem bypass automático (acesso total).
 */
export const useFeaturePermission = (featureKey: string) => {
  const { isPermissionsLoading, canAccess } = useAuth();
  const { isMaster } = usePermissions();

  const hasPermission = useMemo(() => {
    if (isMaster) return true;
    return canAccess(featureKey);
  }, [isMaster, canAccess, featureKey]);

  return {
    hasPermission,
    isLoading: isPermissionsLoading,
  };
};

interface RolePermission {
  id: string;
  company_id: string;
  role: string;
  feature_key: string;
  is_enabled: boolean;
}

/**
 * Hook para gerenciar a matriz completa de permissões (CRUD).
 * Usado pelo RolePermissionsPanel.
 */
export const usePermissionsMatrix = () => {
  const { companyId, isLoading: authLoading } = useCurrentCompanyId();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: permissions, isLoading, refetch } = useQuery({
    queryKey: ['role-permissions', companyId],
    queryFn: async () => {
      if (!companyId) return [];

      const { data, error } = await supabase
        .from('role_permissions')
        .select('*')
        .eq('company_id', companyId);

      if (error) throw error;

      // Auto-seed if no permissions exist
      if (!data || data.length === 0) {
        await supabase.rpc('ensure_default_role_permissions', {
          p_company_id: companyId,
        });

        const { data: seeded, error: seedError } = await supabase
          .from('role_permissions')
          .select('*')
          .eq('company_id', companyId);

        if (seedError) throw seedError;
        return (seeded as RolePermission[]) || [];
      }

      return data as RolePermission[];
    },
    enabled: !!companyId && !authLoading,
    staleTime: 2 * 60 * 1000,
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, is_enabled }: { id: string; is_enabled: boolean }) => {
      if (!companyId) throw new Error('Company ID não disponível');

      const { error } = await supabase
        .from('role_permissions')
        .update({ is_enabled })
        .eq('id', id)
        .eq('company_id', companyId);

      if (error) throw error;
    },
    onMutate: async ({ id, is_enabled }) => {
      await queryClient.cancelQueries({ queryKey: ['role-permissions', companyId] });
      const previous = queryClient.getQueryData<RolePermission[]>(['role-permissions', companyId]);
      queryClient.setQueryData<RolePermission[]>(
        ['role-permissions', companyId],
        (old) => old?.map(p => p.id === id ? { ...p, is_enabled } : p)
      );
      return { previous };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-permission'] });
      toast({ title: 'Permissão atualizada com sucesso' });
    },
    onError: (error, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['role-permissions', companyId], context.previous);
      }
      logger.error('Error toggling permission:', error);
      toast({ title: 'Erro ao atualizar permissão', variant: 'destructive' });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['role-permissions', companyId] });
    },
  });

  // ✅ PERF: Map O(1) indexado por "role:featureKey"
  const permissionsMap = useMemo(() => {
    const map = new Map<string, RolePermission>();
    (permissions || []).forEach(p => {
      map.set(`${p.role}:${p.feature_key}`, p);
    });
    return map;
  }, [permissions]);

  const getPermission = (role: string, featureKey: string): RolePermission | undefined => {
    return permissionsMap.get(`${role}:${featureKey}`);
  };

  return {
    permissions: permissions || [],
    companyId,
    isLoading: authLoading || isLoading,
    togglePermission: toggleMutation.mutate,
    // R-P8.41 p2b-fix: caminho por promise — resolve sempre no sucesso, imune à
    // supressão de callbacks por-chamada do mutate() (v5 só honra a última invocação)
    togglePermissionAsync: toggleMutation.mutateAsync,
    isToggling: toggleMutation.isPending,
    togglingId: toggleMutation.isPending ? (toggleMutation.variables?.id ?? null) : null,
    getPermission,
    refetch,
    FEATURE_LABELS,
    ROLE_LABELS,
    CONFIGURABLE_ROLES,
    FEATURE_KEYS,
    FEATURE_MODULES,
  };
};
